import { Dot } from "@hortis/ui/dot";
import {
  HoverCard,
  HoverCardContent,
  HoverCardTrigger,
} from "@hortis/ui/hover-card";
import { Tag3 } from "@hortis/ui/icons";
import { PhotoPlaceholder } from "@hortis/ui/photo-placeholder";
import { CircularProgress } from "@hortis/ui/progress";
import { Link } from "@tanstack/react-router";
import { NodeViewProps, NodeViewWrapper } from "@tiptap/react";
import { TwicImg } from "@twicpics/components/react";
import { PlantMaterialStatus } from "generated/graphql";
import { ReactNode, useState } from "react";
import {
  PlantMaterialStatusToColor,
  StatusChip,
} from "src/components/status-chip";
import { createMaterialNumber } from "src/features/collection/components/plant-materials/material-number";
import { getMaterialByIdDocument } from "src/features/records/material/graphql";
import { useGqlQuery } from "src/utils/hooks/graphql";
import { materialGroupLabelMap } from "src/utils/label-maps/material-group";
import { getThumbnailPhoto } from "src/utils/photos";
import { twMerge } from "tailwind-merge";
import { MaterialHoverCardContent } from "./material-hover-card-content";

export const MaterialMentionNode = ({
  node,
  link = false,
}: NodeViewProps & { children?: ReactNode; link?: boolean }) => {
  const res = useGqlQuery({
    query: getMaterialByIdDocument,
    variables: { id: node.attrs.id },
  });

  const material = res.data?.material;

  const thumbnail = getThumbnailPhoto(material);

  const [blockHover, setBlockHover] = useState(false);

  const content = (
    <span>
      <span className="mr-1.5">
        {material != null ? (
          <span className="h-4 w-3 inline-flex align-middle pb-0.5 items-center justify-center">
            <Dot color={PlantMaterialStatusToColor[material.status]} />
          </span>
        ) : (
          <CircularProgress size={12} />
        )}
      </span>
      <span className="m-auto text-grey-700">
        {material?.accession != null
          ? createMaterialNumber(
              material.accession?.accessionNumber ?? "",
              material.qualifier,
              "/",
            )
          : node.attrs.label}
      </span>
      <span className="text-grey-500 font-normal truncate ml-1.5 max-w-sm">
        {material?.accession?.scientificName}
      </span>
    </span>
  );

  return (
    <NodeViewWrapper as="span">
      <HoverCard openDelay={300}>
        <HoverCardTrigger
          asChild
          className="mention no-underline! cursor-default items-center rounded-md border border-grey-200 bg-white px-1.5 shadow-xs transition hover:bg-grey-50 py-px"
          data-cy="material-mention"
          onMouseDown={() => {
            setBlockHover(true);
          }}
          onMouseOut={() => {
            setBlockHover(false);
          }}
          onMouseUp={() => {
            setBlockHover(false);
          }}
          onDragStart={() => {
            setBlockHover(true);
          }}
          onDragEnd={() => {
            setBlockHover(false);
          }}
          data-drag-handle
        >
          {link === true && typeof node.attrs.href === "string" ? (
            <Link to={node.attrs.href.replace(window.location.origin, "")}>
              {content}
            </Link>
          ) : (
            content
          )}
        </HoverCardTrigger>
        {material != null && blockHover !== true && (
          <MaterialHoverCardContent material={material} />
        )}
      </HoverCard>
    </NodeViewWrapper>
  );
};
